import React, { useState, useMemo, useEffect } from 'react';
import { Clipboard, Check, CheckCircle, SearchX, Download } from 'lucide-react';
import { getOltLabel, getCtoLabel, getCtoGroupKey, groupMonitoringClients } from '../../utils/networkMonitoring';
import { copyToClipboard } from '../../utils/clipboard';
import Pagination from './Pagination';
import ClientDetailCard from './cards/ClientDetailCard';
import SearchInput from './SearchInput';

const ITEMS_PER_PAGE = 50;

const BadSignalClientsTable = ({ clients, currentUser, theme, threshold, lastUpdated, onFilteredClientsChange, mapSelectedClient }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [copySuccessId, setCopySuccessId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedClient, setSelectedClient] = useState(null);
    const [serviceOrders, setServiceOrders] = useState({});
    const [groupByCto, setGroupByCto] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState('');

    // ⚡ Bolt: Pre-calculate normalized search strings to avoid O(N) string creations on every render/keystroke
    const preparedClients = useMemo(() => {
        if (!clients) return [];
        return clients.map(client => {
            const nameLower = client.name ? client.name.toLowerCase() : '';
            const oltLower = client.olt ? client.olt.toLowerCase() : '';
            return {
                ...client,
                // Concatenate searchable fields for a single O(1) includes check later
                searchString: `${nameLower} ||| ${oltLower} ||| ${getCtoLabel(client).toLowerCase()}`
            };
        });
    }, [clients]);

    // ⚡ Bolt: Filter using the pre-calculated searchString, reducing CPU and GC overhead during typing
    const filteredClients = useMemo(() => {
        if (!preparedClients) return [];
        if (!searchTerm) {
            return preparedClients;
        }
        const term = searchTerm.toLowerCase();
        return preparedClients.filter(client => client.searchString.includes(term));
    }, [preparedClients, searchTerm]);

    useEffect(() => { onFilteredClientsChange?.(filteredClients); }, [filteredClients, onFilteredClientsChange]);
    useEffect(() => { if (mapSelectedClient) setSelectedClient(mapSelectedClient); }, [mapSelectedClient]);


    const groups = useMemo(() => groupByCto ? groupMonitoringClients(filteredClients) : [], [filteredClients, groupByCto]);
    const orderedClients = useMemo(() => groupByCto ? groups.flatMap(group => group.clients) : filteredClients, [filteredClients, groups, groupByCto]);
    const groupCounts = useMemo(() => new Map(groups.map(group => [group.key, group.clients.length])), [groups]);
    const totalPages = Math.ceil(orderedClients.length / ITEMS_PER_PAGE);
    const safePage = Math.min(currentPage, Math.max(1, totalPages));
    const paginatedClients = useMemo(() => {
        const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
        return orderedClients.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [orderedClients, safePage]);

    // Resetar a página para 1 quando o filtro de busca mudar
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, groupByCto]);

    const handleExport = async () => {
        setExporting(true);
        setExportError('');
        try {
            const { exportNetworkMonitoringPdf } = await import('../../utils/networkMonitoringPdf');
            exportNetworkMonitoringPdf({ clients: filteredClients, grouped: groupByCto, searchTerm, threshold, lastUpdated });
        } catch (error) {
            console.error('Erro ao exportar monitoramento:', error);
            setExportError('Não foi possível exportar o PDF. Tente novamente.');
        } finally {
            setExporting(false);
        }
    };

    const handleCopy = async (client) => {
        const rawName = client.name || '';
        let nameOnly = rawName;

        // Encontra a posição do último número na string.
        const lastDigitIndex = rawName.search(/\d(?!.*\d)/);

        if (lastDigitIndex !== -1) {
            // Pega a substring após o último número.
            let potentialName = rawName.substring(lastDigitIndex + 1);
            // Remove hífens e espaços em branco do início.
            potentialName = potentialName.replace(/^[\s-]+\s*/, '');
            if (potentialName) {
                nameOnly = potentialName;
            }
        }

        const success = await copyToClipboard(nameOnly);
        if (success) {
            setCopySuccessId(client.id);
            setTimeout(() => setCopySuccessId(null), 2000);
        }
    };

    const clientDetail = <ClientDetailCard
        key={selectedClient?.id || 'empty'}
        client={selectedClient}
        onClose={() => setSelectedClient(null)}
        currentUser={currentUser}
        serviceOrder={serviceOrders[selectedClient?.id]}
        onServiceOrderCreated={(client, result) => setServiceOrders(previous => ({ ...previous, [client.id]: result }))}
        theme={theme}
    />;
    if (!clients || clients.length === 0) {
        return (
            <>
            {clientDetail}
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow p-8 mt-6 flex flex-col items-center justify-center min-h-[300px] border border-slate-200 dark:border-slate-700">
                <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-full mb-4">
                    <CheckCircle className="h-12 w-12 text-green-500" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">Tudo certo por aqui!</h3>
                <p className="text-slate-500 dark:text-slate-400 text-center max-w-md">
                    Nenhum cliente com sinal ruim encontrado na rede. Aparentemente, a conexão está ótima para todos.
                </p>
            </div>
            </>
        );
    }

    return (
        <>
            {clientDetail}
            <div className="w-full min-w-0 bg-white dark:bg-slate-800 rounded-lg shadow mt-6 overflow-hidden">
                <div className="p-4 space-y-4 border-b border-slate-200 dark:border-slate-700">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
                        Clientes com Sinal Ruim ({filteredClients.length})
                    </h3>
                </div>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="w-full min-w-0 sm:max-w-md">
                        <SearchInput
                            aria-label="Buscar clientes por nome, OLT ou CTO"
                            placeholder="Buscar por nome, OLT ou CTO..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex shrink-0 items-stretch gap-2">
                        <label className="flex shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-600 px-3 py-2.5 text-sm text-slate-600 dark:text-slate-300">
                            <input className="h-4 w-4 accent-indigo-600" type="checkbox" checked={groupByCto} onChange={e => setGroupByCto(e.target.checked)} />
                            Agrupar por CTO
                        </label>
                        <button onClick={handleExport} disabled={exporting || filteredClients.length === 0} className="btn-secondary flex shrink-0 items-center justify-center p-3 disabled:opacity-50" title={exporting ? 'Exportando PDF...' : 'Exportar todos os resultados filtrados em PDF'} aria-label="Exportar PDF" aria-busy={exporting}>
                            <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>
            {exportError && <p role="alert" className="p-4 text-red-600">{exportError}</p>}
            <div className="w-full overflow-x-auto dark-scrollbar" role="region" aria-label="Clientes com sinal ruim" tabIndex={0} style={{ touchAction: 'pan-x pan-y' }}>
                <table className="w-full min-w-[960px] divide-y divide-slate-200 dark:divide-slate-700 table-fixed">
                    <colgroup>
                        {['7%', '30%', '19%', '13%', '9%', '9%', '9%', '4%'].map((width, index) => <col key={index} style={{ width }} />)}
                    </colgroup>
                    <thead className="bg-slate-50 dark:bg-slate-700">
                        <tr>
                            <th scope="col" className="w-20 px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">ID</th>
                            <th scope="col" className="w-auto px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">Nome</th>
                            <th scope="col" className="w-48 px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">Transmissor</th>
                            <th scope="col" className="w-48 px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">CTO</th>
                            <th scope="col" className="w-32 px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">Rx (dBm)</th>
                            <th scope="col" className="w-32 px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">Tx (dBm)</th>
                            <th scope="col" className="w-36 px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-300 uppercase tracking-wider">Temp. (°C)</th>
                            <th scope="col" className="relative w-16 px-4 py-3">
                                <span className="sr-only">Copiar</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-slate-800 divide-y divide-slate-200 dark:divide-slate-700">
                        {filteredClients.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="px-6 py-12 text-center">
                                    <div className="flex flex-col items-center justify-center">
                                        <div className="bg-slate-100 dark:bg-slate-700/50 p-4 rounded-full mb-4">
                                            <SearchX className="h-10 w-10 text-slate-400" />
                                        </div>
                                        <p className="text-slate-600 dark:text-slate-300 font-medium mb-1">Nenhum resultado encontrado para a busca</p>
                                        <p className="text-slate-500 dark:text-slate-400 text-sm mb-4">Tente usar outros termos ou limpe a busca.</p>
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            className="btn-secondary text-sm"
                                        >
                                            Limpar busca
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            paginatedClients.map((client, index) => (
                                <React.Fragment key={client.id}>
                                {groupByCto && (index === 0 || getCtoGroupKey(client) !== getCtoGroupKey(paginatedClients[index - 1])) && (
                                    <tr className="bg-indigo-50 dark:bg-indigo-900/20">
                                        <th scope="rowgroup" colSpan="8" className="px-4 py-3 text-left text-sm font-semibold text-slate-700 dark:text-slate-200 break-words">
                                            OLT: {getOltLabel(client)}{client.oltId ? ` (ID ${client.oltId})` : ''} | CTO: {getCtoLabel(client)} — {groupCounts.get(getCtoGroupKey(client))} cliente(s)
                                        </th>
                                    </tr>
                                )}
                                <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50">
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">{client.id}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                                        <button onClick={() => setSelectedClient(client)} className="block w-full truncate hover:underline text-left" title={client.name}>
                                            {client.name}
                                        </button>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400 truncate" title={getOltLabel(client)}>{getOltLabel(client)}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400 truncate" title={getCtoLabel(client)}>{getCtoLabel(client)}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${client.signal <= -27 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                            {client.signal}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">{client.txPower ?? 'N/A'}</td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-slate-500 dark:text-slate-400">{client.temperature ?? 'N/A'}</td>
                                    <td className="px-2 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button
                                            onClick={() => handleCopy(client)}
                                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                                            title="Copiar nome"
                                            aria-label="Copiar nome"
                                        >
                                            {copySuccessId === client.id ? (
                                                <Check className="h-5 w-5 text-green-500" />
                                            ) : (
                                                <Clipboard className="h-5 w-5" />
                                            )}
                                        </button>
                                    </td>
                                </tr>
                                </React.Fragment>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
             {totalPages > 1 && (
                <div className="p-4 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                    <Pagination
                        currentPage={safePage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </div>
            )}
        </div>
    </>
    );
};

// ⚡ Bolt: Wrap in React.memo to prevent re-renders when parent state (NetworkMonitoringPage) updates but props haven't changed.
export default React.memo(BadSignalClientsTable);
